import { useQuery } from '@tanstack/react-query';

import { parseErrorMessage, timeMeasurement } from '@pkg/utils';

import { callGetRates } from './request';
import { type ExchangeRates } from './types';
import { INITIAL_RATES } from './utilities';

export interface ExchangeRatesQueryProps {
    interval?: number;
    autoUpdate?: boolean;
    disabled?: boolean;
}

export type ExchangeRatesQueryReturn = ReturnType<typeof useExchangeRatesQuery>;

export function useExchangeRatesQuery({
    interval = timeMeasurement.msPerMin,
    autoUpdate = false,
    disabled = false,
}: ExchangeRatesQueryProps) {
    const { data, dataUpdatedAt, error, isLoading, refetch } = useQuery(
        ['exchange-rates-query'],
        ({ signal }) =>
            callGetRates({
                settings: {
                    signal,
                },
            }),
        {
            retry: false,
            keepPreviousData: true,
            enabled: !disabled,
            refetchInterval: autoUpdate ? interval : undefined,
            select: (res) => {
                if (res?.ok) {
                    return res.value;
                }
            },
        },
    );

    const apt = data?.aptos.usd || INITIAL_RATES.apt;
    const eth = data?.ethereum.usd || INITIAL_RATES.eth;
    const matic = data?.['matic-network'].usd || INITIAL_RATES.matic;

    const formattedData: ExchangeRates = {
        usd: 1,
        apt,
        eth,
        matic,
    };

    return {
        rates: formattedData,
        updatedAt: dataUpdatedAt,
        error: error ? parseErrorMessage(error) : undefined,
        loading: isLoading,
        refetch,
    };
}
